/**
 * Admin Authentication Error Handler Service
 * 
 * Provides comprehensive error handling and recovery for admin authentication.
 * Implements graceful error handling, user-friendly messages, fallback mechanisms,
 * and logging for authentication events.
 * 
 * Requirements addressed:
 * - 2.3: Handle token validation failures with clear error messaging
 * - 3.3: Handle graceful logout with user notification
 */

import { adminTokenStorage } from './adminTokenStorage';
import { adminService } from './adminService';

export type AuthErrorType = 
  | 'TOKEN_EXPIRED'
  | 'TOKEN_INVALID'
  | 'TOKEN_MISSING'
  | 'NETWORK_ERROR'
  | 'VALIDATION_FAILED'
  | 'REFRESH_FAILED'
  | 'CONCURRENT_REQUEST_ERROR'
  | 'STORAGE_ERROR'
  | 'API_SERVICE_ERROR'
  | 'UNKNOWN_ERROR';

export type AuthErrorSeverity = 'critical' | 'error' | 'warning' | 'info';

export interface AuthError {
  type: AuthErrorType;
  severity: AuthErrorSeverity;
  message: string;
  persianMessage?: string;
  timestamp: Date;
  recoverable: boolean;
  shouldRedirectToLogin: boolean;
  retryable: boolean;
  context?: Record<string, any>;
  originalError?: Error;
}

export interface RecoveryOptions {
  attemptRefresh?: boolean;
  maxRetries?: number;
  redirectOnFailure?: boolean;
  showNotification?: boolean;
  logError?: boolean;
}

export interface RecoveryResult {
  success: boolean;
  recovered: boolean;
  error?: AuthError;
  action?: 'refreshed' | 'cleared' | 'redirected' | 'retry' | 'none';
}

export interface ErrorLog {
  error: AuthError;
  recoveryAttempted: boolean;
  recoveryResult?: RecoveryResult;
  userAction?: string;
}

class AdminAuthErrorHandler {
  private errorLogs: ErrorLog[] = [];
  private readonly maxErrorLogs = 100;
  private errorHandlers: Map<AuthErrorType, Set<(error: AuthError) => void>> = new Map();
  private globalErrorHandlers: Set<(error: AuthError) => void> = new Set();
  private recoveryAttempts: Map<string, number> = new Map();

  constructor() {
    console.log('AdminAuthErrorHandler: Initialized');
    this.setupGlobalErrorListeners();
  }

  /**
   * Handle authentication error with comprehensive recovery
   * Main entry point for error handling
   */
  async handleError(
    error: Error | AuthError,
    options: RecoveryOptions = {}
  ): Promise<RecoveryResult> {
    const authError = this.normalizeError(error);
    
    // Log the error
    if (options.logError !== false) {
      this.logError(authError);
    }

    // Show user notification
    if (options.showNotification !== false) {
      this.showUserNotification(authError);
    }

    // Notify error handlers
    this.notifyErrorHandlers(authError);

    // Attempt recovery if error is recoverable
    if (authError.recoverable) {
      const recoveryResult = await this.attemptRecovery(authError, options);
      
      // Log recovery attempt
      this.logRecoveryAttempt(authError, recoveryResult);
      
      return recoveryResult;
    }

    // Handle non-recoverable errors
    return this.handleNonRecoverableError(authError, options);
  }

  /**
   * Create error from validation failure
   */
  createValidationError(reason: string, context?: Record<string, any>): AuthError {
    // Check if this is a 401 error from server - if so, should redirect
    const is401Error = context?.error?.statusCode === 401 || context?.error?.status === 401;
    
    return {
      type: 'VALIDATION_FAILED',
      severity: 'error',
      message: `Token validation failed: ${reason}`,
      persianMessage: `Token validation failed: ${reason}`,
      timestamp: new Date(),
      recoverable: !is401Error, // 401 errors are not recoverable
      shouldRedirectToLogin: is401Error, // 401 errors should redirect
      retryable: !is401Error, // 401 errors are not retryable
      ...(context && { context })
    };
  }

  /**
   * Create error from token expiration
   */
  createTokenExpiredError(context?: Record<string, any>): AuthError {
    return {
      type: 'TOKEN_EXPIRED',
      severity: 'warning',
      message: 'Your session has expired. Please log in again.',
      persianMessage: 'Your session has expired. Please log in again.',
      timestamp: new Date(),
      recoverable: false,
      shouldRedirectToLogin: true,
      retryable: false,
      ...(context && { context })
    };
  }

  /**
   * Create error from network failure
   */
  createNetworkError(originalError: Error, context?: Record<string, any>): AuthError {
    return {
      type: 'NETWORK_ERROR',
      severity: 'error',
      message: 'Network error during authentication. Please check your connection.',
      persianMessage: 'Network error during authentication. Please check your connection.',
      timestamp: new Date(),
      recoverable: true,
      shouldRedirectToLogin: false,
      retryable: true,
      ...(context && { context }),
      originalError
    };
  }

  /**
   * Create error from refresh failure
   */
  createRefreshFailedError(reason: string, context?: Record<string, any>): AuthError {
    return {
      type: 'REFRESH_FAILED',
      severity: 'error',
      message: `Token refresh failed: ${reason}`,
      persianMessage: `Token refresh failed: ${reason}`,
      timestamp: new Date(),
      recoverable: true,
      shouldRedirectToLogin: false,
      retryable: true,
      ...(context && { context })
    };
  }

  /**
   * Attempt recovery from authentication error
   */
  private async attemptRecovery(
    error: AuthError,
    options: RecoveryOptions
  ): Promise<RecoveryResult> {
    console.log('AdminAuthErrorHandler: Attempting recovery for error:', error.type);

    // Check retry limits
    const retryKey = `${error.type}_${error.timestamp.getTime()}`;
    const attempts = this.recoveryAttempts.get(retryKey) || 0;
    const maxRetries = options.maxRetries ?? 3;

    if (attempts >= maxRetries) {
      console.log('AdminAuthErrorHandler: Max retry attempts reached');
      return {
        success: false,
        recovered: false,
        error,
        action: 'none'
      };
    }

    this.recoveryAttempts.set(retryKey, attempts + 1);

    // Attempt recovery based on error type
    switch (error.type) {
      case 'TOKEN_EXPIRED':
        return await this.recoverFromExpiredToken(error, options);
      
      case 'TOKEN_INVALID':
        return await this.recoverFromInvalidToken(error, options);
      
      case 'NETWORK_ERROR':
        return await this.recoverFromNetworkError(error, options);
      
      case 'REFRESH_FAILED':
        return await this.recoverFromRefreshFailure(error, options);
      
      case 'VALIDATION_FAILED':
        return await this.recoverFromValidationFailure(error, options);
      
      case 'STORAGE_ERROR':
        return await this.recoverFromStorageError(error, options);
      
      default:
        return await this.recoverFromUnknownError(error, options);
    }
  }

  /**
   * Recover from expired token
   */
  private async recoverFromExpiredToken(
    error: AuthError,
    options: RecoveryOptions
  ): Promise<RecoveryResult> {
    console.log('AdminAuthErrorHandler: Recovering from expired token');

    // Attempt token refresh if enabled
    if (options.attemptRefresh !== false) {
      try {
        const refreshed = await adminService.refreshAdminAuth();
        
        if (refreshed) {
          console.log('AdminAuthErrorHandler: Token refreshed successfully');
          this.showSuccessNotification('Session extended successfully');
          
          return {
            success: true,
            recovered: true,
            action: 'refreshed'
          };
        }
      } catch (refreshError) {
        console.error('AdminAuthErrorHandler: Token refresh failed:', refreshError);
      }
    }

    // Fallback: clear session and redirect
    return await this.clearSessionAndRedirect(error, options);
  }

  /**
   * Recover from invalid token
   */
  private async recoverFromInvalidToken(
    error: AuthError,
    options: RecoveryOptions
  ): Promise<RecoveryResult> {
    console.log('AdminAuthErrorHandler: Recovering from invalid token');

    // Invalid tokens cannot be recovered, must clear and redirect
    return await this.clearSessionAndRedirect(error, options);
  }

  /**
   * Recover from network error
   */
  private async recoverFromNetworkError(
    error: AuthError,
    options: RecoveryOptions
  ): Promise<RecoveryResult> {
    console.log('AdminAuthErrorHandler: Recovering from network error');

    // Check if token is still valid in storage
    const validation = adminTokenStorage.validateToken();
    
    if (validation.isValid) {
      console.log('AdminAuthErrorHandler: Token still valid, network issue only');
      
      // Show retry notification
      this.showWarningNotification('Network error. Retrying...');
      
      return {
        success: true,
        recovered: true,
        action: 'retry'
      };
    }

    // Token is not valid, need to clear session
    return await this.clearSessionAndRedirect(error, options);
  }

  /**
   * Recover from refresh failure
   */
  private async recoverFromRefreshFailure(
    error: AuthError,
    options: RecoveryOptions
  ): Promise<RecoveryResult> {
    console.log('AdminAuthErrorHandler: Recovering from refresh failure');

    // Check if current token is still valid
    const validation = adminTokenStorage.validateToken();
    
    if (validation.isValid && !validation.isExpired) {
      console.log('AdminAuthErrorHandler: Current token still valid despite refresh failure');
      
      return {
        success: true,
        recovered: true,
        action: 'none'
      };
    }

    // Token is expired or invalid, must clear and redirect
    return await this.clearSessionAndRedirect(error, options);
  }

  /**
   * Recover from validation failure
   */
  private async recoverFromValidationFailure(
    error: AuthError,
    options: RecoveryOptions
  ): Promise<RecoveryResult> {
    console.log('AdminAuthErrorHandler: Recovering from validation failure');

    // Attempt to restore token from storage
    try {
      const token = adminTokenStorage.getToken();
      
      if (token) {
        const validation = adminTokenStorage.validateToken();
        
        if (validation.isValid) {
          console.log('AdminAuthErrorHandler: Token restored from storage');
          
          return {
            success: true,
            recovered: true,
            action: 'none'
          };
        }
      }
    } catch (storageError) {
      console.error('AdminAuthErrorHandler: Storage access failed:', storageError);
    }

    // Cannot recover, clear and redirect
    return await this.clearSessionAndRedirect(error, options);
  }

  /**
   * Recover from storage error
   */
  private async recoverFromStorageError(
    error: AuthError,
    _options: RecoveryOptions
  ): Promise<RecoveryResult> {
    console.log('AdminAuthErrorHandler: Recovering from storage error');

    // Try to clear storage and reset
    try {
      adminTokenStorage.clearToken();
      
      this.showWarningNotification('Storage error. Please log in again.');
      
      return {
        success: false,
        recovered: false,
        error,
        action: 'cleared'
      };
    } catch (clearError) {
      console.error('AdminAuthErrorHandler: Failed to clear storage:', clearError);
      
      return {
        success: false,
        recovered: false,
        error,
        action: 'none'
      };
    }
  }

  /**
   * Recover from unknown error
   */
  private async recoverFromUnknownError(
    error: AuthError,
    options: RecoveryOptions
  ): Promise<RecoveryResult> {
    console.log('AdminAuthErrorHandler: Recovering from unknown error');

    // Log detailed error information
    console.error('AdminAuthErrorHandler: Unknown error details:', {
      error,
      context: error.context,
      originalError: error.originalError
    });

    // Attempt generic recovery
    return await this.clearSessionAndRedirect(error, options);
  }

  /**
   * Handle non-recoverable errors
   */
  private async handleNonRecoverableError(
    error: AuthError,
    options: RecoveryOptions
  ): Promise<RecoveryResult> {
    console.log('AdminAuthErrorHandler: Handling non-recoverable error:', error.type);

    if (error.shouldRedirectToLogin || options.redirectOnFailure) {
      return await this.clearSessionAndRedirect(error, options);
    }

    return {
      success: false,
      recovered: false,
      error,
      action: 'none'
    };
  }

  /**
   * Clear session and redirect to login
   */
  private async clearSessionAndRedirect(
    error: AuthError,
    options: RecoveryOptions
  ): Promise<RecoveryResult> {
    console.log('AdminAuthErrorHandler: Clearing session and redirecting');

    try {
      // Clear admin session
      await adminService.clearAdminSession();
      
      // Show notification (but don't wait for it)
      this.showWarningNotification(error.message, error.persianMessage);
      
      // Redirect to login if enabled - redirect immediately without delay (same as user dashboard)
      if (options.redirectOnFailure !== false && error.shouldRedirectToLogin) {
        const { redirectToSessionExpiredLogin } = await import('../utils/sessionExpiredRedirect');
        redirectToSessionExpiredLogin('admin');
        
        return {
          success: false,
          recovered: false,
          error,
          action: 'redirected'
        };
      }
      
      return {
        success: false,
        recovered: false,
        error,
        action: 'cleared'
      };
    } catch (clearError) {
      console.error('AdminAuthErrorHandler: Failed to clear session:', clearError);
      
      // Even if clearing fails, still try to redirect if needed
      if (options.redirectOnFailure !== false && error.shouldRedirectToLogin) {
        const { redirectToSessionExpiredLogin } = await import('../utils/sessionExpiredRedirect');
        redirectToSessionExpiredLogin('admin');
      }
      
      return {
        success: false,
        recovered: false,
        error,
        action: 'none'
      };
    }
  }

  /**
   * Normalize error to AuthError format
   */
  private normalizeError(error: Error | AuthError): AuthError {
    if (this.isAuthError(error)) {
      return error;
    }

    // Convert generic Error to AuthError
    return {
      type: 'UNKNOWN_ERROR',
      severity: 'error',
      message: error.message || 'An unknown authentication error occurred',
      persianMessage: 'Unknown authentication error occurred',
      timestamp: new Date(),
      recoverable: true,
      shouldRedirectToLogin: false,
      retryable: true,
      originalError: error
    };
  }

  /**
   * Check if error is AuthError
   */
  private isAuthError(error: any): error is AuthError {
    return error && typeof error === 'object' && 'type' in error && 'severity' in error;
  }

  /**
   * Log error to internal log
   */
  private logError(error: AuthError): void {
    const errorLog: ErrorLog = {
      error,
      recoveryAttempted: false
    };

    this.errorLogs.push(errorLog);

    // Trim logs if exceeding max
    if (this.errorLogs.length > this.maxErrorLogs) {
      this.errorLogs.shift();
    }

    // Log to console
    console.error('AdminAuthErrorHandler: Error logged:', {
      type: error.type,
      severity: error.severity,
      message: error.message,
      timestamp: error.timestamp,
      context: error.context
    });

    // Dispatch error event for external logging
    window.dispatchEvent(new CustomEvent('admin:auth-error', {
      detail: error
    }));
  }

  /**
   * Log recovery attempt
   */
  private logRecoveryAttempt(error: AuthError, result: RecoveryResult): void {
    const lastLog = this.errorLogs[this.errorLogs.length - 1];
    
    if (lastLog && lastLog.error === error) {
      lastLog.recoveryAttempted = true;
      lastLog.recoveryResult = result;
    }

    console.log('AdminAuthErrorHandler: Recovery attempt logged:', {
      errorType: error.type,
      success: result.success,
      recovered: result.recovered,
      action: result.action
    });
  }

  /**
   * Show user notification
   */
  private showUserNotification(error: AuthError): void {
    const notificationType = this.getNotificationType(error.severity);
    
    window.dispatchEvent(new CustomEvent('admin:notification', {
      detail: {
        type: notificationType,
        message: error.message,
        persianMessage: error.persianMessage,
        duration: this.getNotificationDuration(error.severity)
      }
    }));
  }

  /**
   * Show success notification
   */
  private showSuccessNotification(message: string, persianMessage?: string): void {
    window.dispatchEvent(new CustomEvent('admin:notification', {
      detail: {
        type: 'success',
        message,
        persianMessage: persianMessage || message,
        duration: 3000
      }
    }));
  }

  /**
   * Show warning notification
   */
  private showWarningNotification(message: string, persianMessage?: string): void {
    window.dispatchEvent(new CustomEvent('admin:notification', {
      detail: {
        type: 'warning',
        message,
        persianMessage: persianMessage || message,
        duration: 5000
      }
    }));
  }

  /**
   * Get notification type from severity
   */
  private getNotificationType(severity: AuthErrorSeverity): string {
    switch (severity) {
      case 'critical':
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'error';
    }
  }

  /**
   * Get notification duration from severity
   */
  private getNotificationDuration(severity: AuthErrorSeverity): number {
    switch (severity) {
      case 'critical':
        return 10000;
      case 'error':
        return 7000;
      case 'warning':
        return 5000;
      case 'info':
        return 3000;
      default:
        return 5000;
    }
  }

  /**
   * Notify error handlers
   */
  private notifyErrorHandlers(error: AuthError): void {
    // Notify type-specific handlers
    const typeHandlers = this.errorHandlers.get(error.type);
    if (typeHandlers) {
      typeHandlers.forEach(handler => {
        try {
          handler(error);
        } catch (handlerError) {
          console.error('AdminAuthErrorHandler: Error in type handler:', handlerError);
        }
      });
    }

    // Notify global handlers
    this.globalErrorHandlers.forEach(handler => {
      try {
        handler(error);
      } catch (handlerError) {
        console.error('AdminAuthErrorHandler: Error in global handler:', handlerError);
      }
    });
  }

  /**
   * Register error handler for specific error type
   */
  onError(type: AuthErrorType, handler: (error: AuthError) => void): () => void {
    if (!this.errorHandlers.has(type)) {
      this.errorHandlers.set(type, new Set());
    }
    
    this.errorHandlers.get(type)!.add(handler);
    
    // Return unsubscribe function
    return () => {
      this.errorHandlers.get(type)?.delete(handler);
    };
  }

  /**
   * Register global error handler
   */
  onAnyError(handler: (error: AuthError) => void): () => void {
    this.globalErrorHandlers.add(handler);
    
    // Return unsubscribe function
    return () => {
      this.globalErrorHandlers.delete(handler);
    };
  }

  /**
   * Get error logs
   */
  getErrorLogs(): ErrorLog[] {
    return [...this.errorLogs];
  }

  /**
   * Clear error logs
   */
  clearErrorLogs(): void {
    this.errorLogs = [];
    console.log('AdminAuthErrorHandler: Error logs cleared');
  }

  /**
   * Setup global error listeners
   */
  private setupGlobalErrorListeners(): void {
    // Listen for admin auth failed events
    window.addEventListener('admin:auth-failed', ((event: CustomEvent) => {
      // CRITICAL: Check if redirect is already in progress to prevent page freezing
      if ((window as any).__sessionRedirectInProgress) {
        console.log('AdminAuthErrorHandler: Redirect already in progress, ignoring admin auth failed event');
        return;
      }

      // When server returns 401, redirect immediately to admin login
      // This matches the behavior of user dashboard
      const detail = event.detail || {};
      const is401Error = detail.error?.statusCode === 401 || detail.error?.status === 401;
      
      if (is401Error) {
        console.log('AdminAuthErrorHandler: 401 error detected, redirecting to admin login immediately');
        
        // Set global flag to prevent other handlers from redirecting
        (window as any).__sessionRedirectInProgress = true;
        
        try {
          adminService.clearAdminSession();
        } catch (err) {
          console.error('AdminAuthErrorHandler: Failed to clear admin session:', err);
        }
        
        void import('../utils/sessionExpiredRedirect').then(({ redirectToSessionExpiredLogin }) => {
          redirectToSessionExpiredLogin('admin');
        });
        return;
      }
      
      // For other errors, use normal error handling
      const error = this.createValidationError('Authentication failed', detail);
      this.handleError(error, { redirectOnFailure: true });
    }) as EventListener);

    // Listen for token expired events
    window.addEventListener('admin:token-expired', (() => {
      // CRITICAL: Check if redirect is already in progress to prevent page freezing
      if ((window as any).__sessionRedirectInProgress) {
        console.log('AdminAuthErrorHandler: Redirect already in progress, ignoring token expired event');
        return;
      }

      // Set global flag to prevent other handlers from redirecting
      (window as any).__sessionRedirectInProgress = true;

      const error = this.createTokenExpiredError();
      this.handleError(error, { redirectOnFailure: true });
    }) as EventListener);
  }
}

// Export singleton instance
export const adminAuthErrorHandler = new AdminAuthErrorHandler();
